import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  User, Building2, Briefcase, Target, Users as UsersIcon, BookOpen,
  Mail, Phone, Calendar, MessageSquare, ArrowRight, ArrowLeft,
  CheckCircle2, Send, MapPin, Zap, Download, MessageCircle, Linkedin, Youtube, Instagram,
} from 'lucide-react';
import { SOCIAL } from '../../data/content';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = ['HR / L&D Manager', 'CTO / VP Engineering', 'CXO / Founder', 'Data Team Lead', 'Individual / Freelancer', 'Other'];
const PURPOSES = ['Corporate Training', 'Speaking Engagement', '1:1 Coaching', 'Consulting', 'Partnership', 'Just want to connect'];
const TEAM_SIZES = ['Just me', '2–10', '11–50', '50–200', '200+'];
const TOPIC_OPTS = ['GenAI & LLMs', 'Agentic AI', 'Claude Code', 'RAG & Vector DB', 'MLOps', 'Data Science', 'NLP', 'AI Strategy / CXO', 'Custom'];
const TIMELINES = ['ASAP', 'This month', 'Next quarter', 'Just exploring'];

export default function Contact() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    full_name: '', company: '', role: '',
    purposes: [], team_size: '', topics: [],
    email: '', phone: '', timeline: '', message: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMulti = (k, v) => setForm((f) => ({
    ...f,
    [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.full_name.trim()) e.full_name = 'Please enter your name';
      if (!form.company.trim()) e.company = 'Please enter your company';
      if (!form.role) e.role = 'Please select a role';
    }
    if (s === 3) {
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep(step + 1); };
  const back = () => setStep(step - 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!validate(3)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setSubmitted(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error(err);
      }
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-14 max-w-3xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal mb-4">
            — Contact · Booking —
          </p>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter">
            Let's Build<br /><span className="text-teal">Something Together.</span>
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
            Whether it's training your team, a speaking gig, or just a conversation about AI —
            I'm all ears. I respond within <span className="text-white font-semibold">24 hours</span>.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="glass-strong rounded-3xl p-8 md:p-10 relative overflow-hidden" style={{ borderColor: 'rgba(0,245,212,0.15)' }}>
              {/* progress */}
              {!submitted && (
                <div className="flex items-center gap-3 mb-8">
                  {[1, 2, 3].map((i) => (
                    <React.Fragment key={i}>
                      <div
                        className={`flex items-center gap-2 ${step >= i ? 'text-teal' : 'text-white/30'}`}
                      >
                        <div className={`w-8 h-8 rounded-full border flex items-center justify-center font-mono text-xs ${
                          step >= i ? 'border-teal bg-teal/10' : 'border-white/15'
                        }`}>
                          {step > i ? <CheckCircle2 size={14} /> : i}
                        </div>
                        <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em]">
                          {['About You', 'Your Need', 'Connect'][i - 1]}
                        </span>
                      </div>
                      {i < 3 && <div className={`flex-1 h-px ${step > i ? 'bg-teal' : 'bg-white/10'}`} />}
                    </React.Fragment>
                  ))}
                </div>
              )}

              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, rotateY: 90 }}
                    animate={{ opacity: 1, rotateY: 0 }}
                    transition={{ duration: 0.6 }}
                    className="py-12 text-center"
                    data-testid="form-success"
                  >
                    <div className="inline-flex w-20 h-20 rounded-full bg-teal/10 border border-teal/40 items-center justify-center mb-6">
                      <CheckCircle2 size={40} className="text-teal" />
                    </div>
                    <h3 className="font-display font-black text-3xl md:text-4xl text-white tracking-tight">
                      Message received, {form.full_name.split(' ')[0]}!
                    </h3>
                    <p className="mt-4 text-white/70">
                      I'll be in touch within 24 hours. While you wait —
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                      <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" className="chip hover:border-[#FF0000]/50">
                        <Youtube size={13} /> Watch on YouTube
                      </a>
                      <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" className="chip hover:border-[#0A66C2]/50">
                        <Linkedin size={13} /> Connect on LinkedIn
                      </a>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    key={`step-${step}`}
                    onSubmit={submit}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                    data-testid={`form-step-${step}`}
                  >
                    {step === 1 && (
                      <>
                        <h3 className="font-display font-bold text-2xl text-white mb-6">Tell me about you.</h3>

                        <Field icon={User} label="Full Name" error={errors.full_name}>
                          <input
                            className="u-input"
                            value={form.full_name}
                            onChange={(e) => set('full_name', e.target.value)}
                            placeholder="Jane Doe"
                            data-testid="input-full-name"
                          />
                        </Field>

                        <Field icon={Building2} label="Company" error={errors.company}>
                          <input
                            className="u-input"
                            value={form.company}
                            onChange={(e) => set('company', e.target.value)}
                            placeholder="Acme Corp"
                            data-testid="input-company"
                          />
                        </Field>

                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Briefcase size={14} className="text-teal" />
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Your Role</label>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {ROLES.map((r) => (
                              <button
                                type="button"
                                key={r}
                                onClick={() => set('role', r)}
                                data-testid={`chip-role-${r.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                className={`chip ${form.role === r ? 'active' : ''}`}
                              >
                                {r}
                              </button>
                            ))}
                          </div>
                          {errors.role && <p className="mt-2 text-xs text-red-400">{errors.role}</p>}
                        </div>

                        <div className="flex justify-end pt-4">
                          <button
                            type="button"
                            onClick={next}
                            data-testid="form-next-1"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-teal text-[#0a0a0f] font-mono text-xs uppercase tracking-[0.15em] font-bold rounded-full hover:bg-white transition-colors"
                          >
                            Next <ArrowRight size={14} />
                          </button>
                        </div>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <h3 className="font-display font-bold text-2xl text-white mb-6">What do you need?</h3>

                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Target size={14} className="text-teal" />
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Reaching out for (multi)</label>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {PURPOSES.map((p) => (
                              <button
                                type="button"
                                key={p}
                                onClick={() => toggleMulti('purposes', p)}
                                className={`chip ${form.purposes.includes(p) ? 'active' : ''}`}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <UsersIcon size={14} className="text-teal" />
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Team Size</label>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {TEAM_SIZES.map((t) => (
                              <button
                                type="button"
                                key={t}
                                onClick={() => set('team_size', t)}
                                className={`chip ${form.team_size === t ? 'active' : ''}`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <BookOpen size={14} className="text-teal" />
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Topics of Interest (multi)</label>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {TOPIC_OPTS.map((t) => (
                              <button
                                type="button"
                                key={t}
                                onClick={() => toggleMulti('topics', t)}
                                className={`chip ${form.topics.includes(t) ? 'active' : ''}`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex justify-between pt-4">
                          <button
                            type="button"
                            onClick={back}
                            className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-white/80 font-mono text-xs uppercase tracking-[0.15em] rounded-full hover:border-white/40"
                          >
                            <ArrowLeft size={14} /> Back
                          </button>
                          <button
                            type="button"
                            onClick={next}
                            data-testid="form-next-2"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-teal text-[#0a0a0f] font-mono text-xs uppercase tracking-[0.15em] font-bold rounded-full hover:bg-white"
                          >
                            Next <ArrowRight size={14} />
                          </button>
                        </div>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <h3 className="font-display font-bold text-2xl text-white mb-6">Let's connect.</h3>

                        <Field icon={Mail} label="Email" error={errors.email}>
                          <input
                            className="u-input"
                            type="email"
                            value={form.email}
                            onChange={(e) => set('email', e.target.value)}
                            placeholder="you@company.com"
                            data-testid="input-email"
                          />
                        </Field>

                        <Field icon={Phone} label="Phone / WhatsApp (optional)">
                          <input
                            className="u-input"
                            value={form.phone}
                            onChange={(e) => set('phone', e.target.value)}
                            placeholder="+91 98765 43210"
                            data-testid="input-phone"
                          />
                        </Field>

                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar size={14} className="text-teal" />
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Preferred Timeline</label>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {TIMELINES.map((t) => (
                              <button
                                type="button"
                                key={t}
                                onClick={() => set('timeline', t)}
                                className={`chip ${form.timeline === t ? 'active' : ''}`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>

                        <Field icon={MessageSquare} label="Anything else?">
                          <textarea
                            className="u-input min-h-[100px] resize-none"
                            value={form.message}
                            onChange={(e) => set('message', e.target.value)}
                            placeholder="Tell me more..."
                            rows={4}
                            data-testid="input-message"
                          />
                        </Field>

                        {errors.submit && <p className="text-sm text-red-400">{errors.submit}</p>}

                        <div className="flex justify-between pt-4">
                          <button
                            type="button"
                            onClick={back}
                            className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-white/80 font-mono text-xs uppercase tracking-[0.15em] rounded-full hover:border-white/40"
                          >
                            <ArrowLeft size={14} /> Back
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            data-testid="form-submit"
                            className="inline-flex items-center gap-2 px-7 py-3 bg-teal text-[#0a0a0f] font-mono text-xs uppercase tracking-[0.15em] font-bold rounded-full hover:bg-white disabled:opacity-50"
                          >
                            {loading ? 'Sending...' : <>Send Message <Send size={14} /></>}
                          </button>
                        </div>
                      </>
                    )}
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Contact card */}
          <div className="lg:col-span-5">
            <div className="glass rounded-3xl p-8 md:p-10 h-full relative border-l-4" style={{ borderLeftColor: '#00f5d4' }}>
              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-teal mt-1" />
                  <div>
                    <div className="font-display font-bold text-white">Based in Berlin</div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/50 mt-1">Trains globally · onsite & virtual</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap size={18} className="text-teal mt-1" />
                  <div>
                    <div className="font-display font-bold text-white">Avg. Response &lt; 24h</div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/50 mt-1">Weekdays · Berlin Time (CET)</div>
                  </div>
                </div>
              </div>

              <div className="divider-hair mb-6" />

              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Connect with me</p>
              <div className="space-y-1 mb-8">
                <SocialRow href={SOCIAL.linkedin} Icon={Linkedin} color="#0A66C2" handle="linkedin.com/in/parthsharma1011" />
                <SocialRow href={SOCIAL.youtube} Icon={Youtube} color="#FF0000" handle="@beardandbinary" />
                <SocialRow href={SOCIAL.instagram} Icon={Instagram} color="#E1306C" handle="@beardandbinary" />
              </div>

              <div className="divider-hair mb-6" />

              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Or jump straight in</p>
              <div className="space-y-3">
                <a
                  href={SOCIAL.calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="card-calendly"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-teal text-[#0a0a0f] font-mono text-xs uppercase tracking-[0.15em] font-bold hover:bg-white transition-colors"
                >
                  <Calendar size={15} /> Schedule a 30-min Call
                </a>
                <a
                  href={`mailto:${SOCIAL.email}?subject=Training%20Brochure%20Request`}
                  data-testid="card-brochure"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/15 text-white/90 font-mono text-xs uppercase tracking-[0.15em] hover:border-teal hover:text-teal transition-colors"
                >
                  <Download size={15} /> Download Brochure
                </a>
                <a
                  href={SOCIAL.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="card-whatsapp"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/15 text-white/90 font-mono text-xs uppercase tracking-[0.15em] hover:border-teal hover:text-teal transition-colors"
                >
                  <MessageCircle size={15} /> Email / Message Me
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ icon: Icon, label, children, error }) {
  return (
    <div>
      <div className="relative">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
          <span className="w-1.5 h-1.5 rounded-full bg-teal/70" />
        </div>
        <div className="flex items-center gap-2 mb-1">
          <Icon size={13} className="text-teal" />
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</label>
        </div>
        {children}
      </div>
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}

function SocialRow({ href, Icon, color, handle }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between py-2.5 border-b border-white/5 transition-all hover:pl-2"
    >
      <div className="flex items-center gap-3">
        <Icon size={17} style={{ color }} strokeWidth={1.75} />
        <span className="font-mono text-xs tracking-wide text-white/85 group-hover:text-teal">{handle}</span>
      </div>
      <span className="text-white/30 group-hover:text-teal transition-colors">→</span>
    </a>
  );
}
